import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Sparkle, Clock, TrendUp, TrendDown, Minus, Heart, Trash, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShareBar } from "@/components/ShareBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const KIND_COLOUR = {
  handover: "border-l-[#6B3FA0]", huddle: "border-l-[#B77B00]", work: "border-l-[#002FA7]",
  break: "border-l-[#0B5C2E]", lunch: "border-l-[#B3241B]", winddown: "border-l-[#475569]",
};

const Trend = ({ trend }) => {
  const Icon = trend.direction === "up" ? TrendUp : trend.direction === "down" ? TrendDown : Minus;
  const tone = trend.direction === "up" ? "text-[#0B5C2E]" : trend.direction === "down" ? "text-[#B3241B]" : "text-slate-500";
  return (
    <p className={`text-sm font-semibold ${tone}`} data-testid="teamwork-trend">
      <Icon size={15} className="mr-1 inline align-[-2px]" />
      This week {trend.this_week ?? "—"}% · last week {trend.last_week ?? "—"}%
    </p>
  );
};

export default function Teamwork() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [people, setPeople] = useState([]);
  const [draft, setDraft] = useState(null);
  const [crew, setCrew] = useState({ name: "", site: "", shift: "" });
  const [note, setNote] = useState({ to: "", text: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/teamwork").then(({ data: d }) => setData(d)).catch(oops);
    api.get("/teamwork/mates").then(({ data: d }) => setPeople(d.mates || [])).catch(() => setPeople([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const suggest = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/teamwork/crews/${data.crew_id}/rhythm/suggest`);
      setDraft(d);
      toast.success(`${d.engine_label} laid out a rhythm — save it if it fits`);
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.put(`/teamwork/crews/${data.crew_id}/rhythm`, { blocks: draft.blocks, line: draft.line || "" });
      toast.success("Rhythm saved");
      setDraft(null); load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const addCrew = async () => {
    setBusy(true);
    try { await api.post("/teamwork/crews", crew); toast.success("Crew added"); setCrew({ name: "", site: "", shift: "" }); load(); }
    catch (e) { oops(e); } finally { setBusy(false); }
  };

  const sendKudos = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/teamwork/kudos", { to_user_id: note.to, note: note.text });
      toast.success(`Kudos to ${d.to_name} · +${d.earned} points`);
      setNote({ to: "", text: "" }); load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const refresh = async () => {
    setBusy(true);
    try { await api.post("/teamwork/refresh"); toast.success("Board refreshed"); load(); }
    catch (e) { oops(e); } finally { setBusy(false); }
  };

  if (!data) return <p className="py-10 text-center text-sm text-slate-500">Loading the crew rhythm…</p>;

  const blocks = draft?.blocks || data.rhythm.blocks;

  return (
    <div className="space-y-6" data-testid="teamwork-page">
      <div>
        <p className="overline text-[#B3241B]">Teamwork</p>
        <h1 className="font-display mt-1 flex items-center gap-2 text-3xl font-bold tracking-tight text-[#0F172A] sm:text-4xl">
          <Users size={30} weight="duotone" className="text-[#002FA7]" /> Crew rhythm
        </h1>
        <p className="mt-2 text-sm text-slate-600">{data.rhythm.line || "The shape of the working day."}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-4" data-testid="teamwork-signals">
        {[["In step", `${data.signals.score}%`], ["On shift", data.signals.on_shift],
          ["Breaks taken", data.signals.breaks_taken], ["Windows missed", data.signals.breaks_missed]].map(([label, value]) => (
          <div key={label} className="border border-border bg-white p-3">
            <p className="overline text-slate-500">{label}</p>
            <p className="mt-1 text-xl font-bold text-[#0F172A]">{value}</p>
          </div>
        ))}
      </div>
      <p className="text-sm leading-relaxed text-slate-700" data-testid="teamwork-read">{data.read}</p>
      <Trend trend={data.trend} />

      <div className="border border-[#002FA7] bg-white p-4" data-testid="teamwork-now">
        <p className="overline text-[#002FA7]">
          <Clock size={13} className="mr-1 inline align-[-2px]" />Right now · {data.rhythm.clock}
        </p>
        <p className="mt-1 text-base font-semibold text-[#0F172A]">
          {data.rhythm.now ? data.rhythm.now.label : "Off shift"}
        </p>
        {data.rhythm.next && (
          <p className="text-sm text-slate-600" data-testid="teamwork-next">
            Next: {data.rhythm.next.label} at {data.rhythm.next.start}
          </p>
        )}
        <div className="mt-3">
          <ShareBar url={`${window.location.origin}/teamwork`}
            text={`Our crew rhythm today — in step ${data.signals.score}%`}
            testid="teamwork-share" compact instagram />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {data.can_manage && (
          <>
            <Button size="sm" variant="outline" data-testid="teamwork-suggest" onClick={suggest} disabled={busy}>
              <Sparkle size={14} className="mr-1.5" /> Let AI lay out the rhythm
            </Button>
            <Button size="sm" variant="outline" data-testid="teamwork-refresh" onClick={refresh} disabled={busy}>
              Refresh the public board
            </Button>
          </>
        )}
        <Link to="/notices" data-testid="teamwork-to-notices"
          className="tap-safe border border-border px-3 py-2 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]">
          House notices
        </Link>
      </div>

      {draft && (
        <div className="border border-[#B77B00] bg-[#FFFBEB] p-4" data-testid="teamwork-draft">
          <p className="text-sm font-semibold text-[#0F172A]">{draft.engine_label} suggests this rhythm</p>
          {draft.line && <p className="mt-1 text-sm text-slate-700">{draft.line}</p>}
          <div className="mt-3 flex gap-2">
            <Button size="sm" data-testid="teamwork-draft-save" onClick={saveDraft} disabled={busy}>Use it</Button>
            <Button size="sm" variant="outline" data-testid="teamwork-draft-discard" onClick={() => setDraft(null)}>Leave it</Button>
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="teamwork-blocks">
        {blocks.map((b, i) => (
          <div key={i} data-testid={`teamwork-block-${i}`}
            className={`border border-border border-l-4 bg-white p-3 ${KIND_COLOUR[b.kind] || "border-l-slate-300"} ${
              data.rhythm.now && data.rhythm.now.start === b.start ? "ring-2 ring-[#002FA7]" : ""}`}>
            <p className="font-mono text-xs text-slate-500">{b.start} – {b.end}</p>
            <p className="text-sm font-semibold text-[#0F172A]">{b.label}</p>
            <p className="overline text-[0.6rem] text-slate-400">{b.kind}</p>
          </div>
        ))}
      </div>

      <div className="border border-border bg-white p-4" data-testid="teamwork-kudos">
        <p className="overline text-slate-500">
          <Heart size={13} className="mr-1 inline align-[-2px] text-[#B3241B]" />Good rhythm kudos
          · +{data.kudos_points} points
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_2fr_auto]">
          <select data-testid="kudos-person" value={note.to} onChange={(e) => setNote({ ...note, to: e.target.value })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            <option value="">Who kept us in step?</option>
            {people.filter((p) => p.id !== user?.id).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <Input data-testid="kudos-note" placeholder="What did they do?" value={note.text}
            onChange={(e) => setNote({ ...note, text: e.target.value })} />
          <Button size="sm" data-testid="kudos-send" onClick={sendKudos} disabled={busy || !note.to}>Send it</Button>
        </div>
        <ul className="mt-3 space-y-1 text-sm text-slate-700" data-testid="kudos-list">
          {(data.kudos || []).slice(0, 6).map((k, i) => (
            <li key={i}>
              <span className="font-semibold text-[#0F172A]">{k.from_name}</span> → {k.to_name}
              {k.note ? ` · ${k.note}` : ""} <span className="text-slate-400">{k.day}</span>
            </li>
          ))}
          {(data.kudos || []).length === 0 && <li className="text-slate-500">No kudos yet — go first.</li>}
        </ul>
      </div>

      {data.can_manage && (
        <div className="border border-border bg-white p-4" data-testid="teamwork-crews">
          <p className="overline text-slate-500">Crews</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-700">
            {data.crews.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2" data-testid={`crew-${c.id}`}>
                <span>{c.name} · {c.site || "—"} · {c.shift || "—"} · {(c.members || []).length} on the list</span>
                {c.key !== "day-shift" && (
                  <button type="button" data-testid={`crew-delete-${c.id}`}
                    onClick={async () => { try { await api.delete(`/teamwork/crews/${c.id}`); load(); } catch (e) { oops(e); } }}
                    className="tap-safe text-xs font-semibold text-[#B3241B]"><Trash size={14} /></button>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-3 grid gap-2 sm:grid-cols-[2fr_1fr_1fr_auto]">
            <Input data-testid="crew-name" placeholder="Crew name" value={crew.name}
              onChange={(e) => setCrew({ ...crew, name: e.target.value })} />
            <Input data-testid="crew-site" placeholder="Site" value={crew.site}
              onChange={(e) => setCrew({ ...crew, site: e.target.value })} />
            <Input data-testid="crew-shift" placeholder="Shift hours" value={crew.shift}
              onChange={(e) => setCrew({ ...crew, shift: e.target.value })} />
            <Button size="sm" data-testid="crew-add" onClick={addCrew} disabled={busy || crew.name.trim().length < 2}>
              <Plus size={14} />
            </Button>
          </div>
        </div>
      )}

      {data.trend.history?.length > 1 && (
        <p className="text-xs text-slate-500" data-testid="teamwork-history">
          Scores: {data.trend.history.map((h) => `${h.day.slice(5)} ${h.score}%`).join(" · ")}
        </p>
      )}
      {BACKEND ? null : null}
    </div>
  );
}
